import React, { useState, useCallback } from 'react';
import { CheckCircle2, XCircle, HelpCircle, RotateCcw, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';

export function QuizSection({ quiz, onComplete, existingScore, moduleName }) {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(existingScore !== null);
  const [score, setScore] = useState(existingScore);

  const handleAnswer = useCallback((questionId, answer) => {
    if (submitted) return;
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
  }, [submitted]);

  const handleSubmit = useCallback(() => {
    let correct = 0;
    quiz.questions.forEach(q => {
      const userAnswer = answers[q.id];
      if (q.type === 'mcq' && userAnswer === String(q.correctAnswer)) correct++;
      if (q.type === 'truefalse' && userAnswer === String(q.correctAnswer)) correct++;
    });
    const pct = Math.round((correct / quiz.questions.length) * 100);
    setScore(pct);
    setSubmitted(true);
    onComplete(quiz.id, pct);
  }, [answers, quiz, onComplete]);

  const handleRetry = useCallback(() => {
    setAnswers({});
    setSubmitted(false);
    setScore(null);
  }, []);

  const allAnswered = quiz.questions.every(q => answers[q.id] !== undefined);

  const getCorrectLabel = (q) => {
    if (q.type === 'mcq') return q.options[q.correctAnswer];
    return q.correctAnswer ? 'True' : 'False';
  };

  return (
    <div data-testid="quiz-section" className="flex-1 overflow-y-auto">
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-8 md:py-12">
        {moduleName && (
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-500 mb-2">{moduleName}</p>
        )}
        <div className="flex items-center gap-3 mb-8">
          <HelpCircle className="h-6 w-6 text-amber-500" />
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-zinc-900 dark:text-zinc-100 tracking-tight">{quiz.title}</h1>
        </div>

        {submitted && (
          <div
            data-testid="quiz-score"
            className={cn(
              "mb-8 p-4 rounded-lg border flex items-center justify-between",
              score >= 70
                ? "bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-800"
                : "bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800"
            )}
          >
            <div className="flex items-center gap-3">
              {score >= 70 ? (
                <CheckCircle2 className="h-6 w-6 text-emerald-500" />
              ) : (
                <XCircle className="h-6 w-6 text-red-500" />
              )}
              <div>
                <p className="font-semibold text-zinc-900 dark:text-zinc-100">
                  {score >= 70 ? 'Great job!' : 'Keep studying!'}
                </p>
                <p className="text-sm text-zinc-600 dark:text-zinc-400">
                  You scored {score}% ({Math.round(score * quiz.questions.length / 100)}/{quiz.questions.length} correct)
                </p>
              </div>
            </div>
            <Button data-testid="quiz-retry-btn" variant="outline" size="sm" onClick={handleRetry} className="flex items-center gap-1">
              <RotateCcw className="h-3.5 w-3.5" />
              Retry
            </Button>
          </div>
        )}

        <div className="space-y-6">
          {quiz.questions.map((q, idx) => {
            const userAnswer = answers[q.id];
            const isCorrect = submitted && (
              (q.type === 'mcq' && userAnswer === String(q.correctAnswer)) ||
              (q.type === 'truefalse' && userAnswer === String(q.correctAnswer))
            );
            const isWrong = submitted && userAnswer !== undefined && !isCorrect;

            return (
              <div
                key={q.id}
                data-testid={`quiz-question-${q.id}`}
                className={cn(
                  "p-5 rounded-lg border transition-all duration-200",
                  submitted && isCorrect && "border-emerald-300 bg-emerald-50/50 dark:bg-emerald-950/20 dark:border-emerald-800",
                  submitted && isWrong && "border-red-300 bg-red-50/50 dark:bg-red-950/20 dark:border-red-800",
                  !submitted && "border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900"
                )}
              >
                <div className="flex items-start gap-3 mb-4">
                  <span className="shrink-0 w-7 h-7 rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center text-xs font-bold text-zinc-600 dark:text-zinc-300">
                    {idx + 1}
                  </span>
                  <div className="flex-1">
                    <p className="font-medium text-zinc-900 dark:text-zinc-100">{q.question}</p>
                    <Badge variant="outline" className="mt-1 text-[10px]">
                      {q.type === 'mcq' ? 'Multiple Choice' : 'True / False'}
                    </Badge>
                  </div>
                  {submitted && (
                    isCorrect ? <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" />
                    : isWrong ? <XCircle className="h-5 w-5 text-red-500 shrink-0" />
                    : null
                  )}
                </div>

                {q.type === 'mcq' ? (
                  <RadioGroup
                    value={userAnswer || ''}
                    onValueChange={(val) => handleAnswer(q.id, val)}
                    className="space-y-2 ml-10"
                    disabled={submitted}
                  >
                    {q.options.map((opt, oi) => {
                      const isThisCorrect = submitted && oi === q.correctAnswer;
                      const isThisSelected = userAnswer === String(oi);
                      return (
                        <label
                          key={oi}
                          data-testid={`quiz-option-${q.id}-${oi}`}
                          className={cn(
                            "flex items-center gap-3 p-3 rounded-md border cursor-pointer transition-all duration-200",
                            !submitted && "hover:bg-zinc-50 dark:hover:bg-zinc-800 border-zinc-200 dark:border-zinc-700",
                            !submitted && isThisSelected && "border-blue-400 bg-blue-50 dark:bg-blue-950/30",
                            submitted && isThisCorrect && "border-emerald-400 bg-emerald-50 dark:bg-emerald-950/30",
                            submitted && isThisSelected && !isThisCorrect && "border-red-400 bg-red-50 dark:bg-red-950/30",
                            submitted && !isThisCorrect && !isThisSelected && "opacity-50",
                            submitted && "cursor-default"
                          )}
                        >
                          <RadioGroupItem value={String(oi)} id={`${q.id}-${oi}`} />
                          <span className="text-sm flex-1">{opt}</span>
                          {submitted && isThisCorrect && (
                            <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                          )}
                        </label>
                      );
                    })}
                  </RadioGroup>
                ) : (
                  <div className="flex gap-3 ml-10">
                    {['true', 'false'].map(val => {
                      const isThisCorrect = submitted && val === String(q.correctAnswer);
                      const isThisSelected = userAnswer === val;
                      return (
                        <button
                          key={val}
                          data-testid={`quiz-tf-${q.id}-${val}`}
                          disabled={submitted}
                          onClick={() => handleAnswer(q.id, val)}
                          className={cn(
                            "flex-1 py-3 px-4 rounded-md border text-sm font-medium transition-all duration-200 flex items-center justify-center gap-2",
                            !submitted && "hover:bg-zinc-50 dark:hover:bg-zinc-800 border-zinc-200 dark:border-zinc-700",
                            !submitted && isThisSelected && "border-blue-400 bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300",
                            submitted && isThisCorrect && "border-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700",
                            submitted && isThisSelected && !isThisCorrect && "border-red-400 bg-red-50 dark:bg-red-950/30 text-red-700",
                            submitted && !isThisCorrect && !isThisSelected && "opacity-50",
                            submitted && "cursor-default"
                          )}
                        >
                          {val === 'true' ? 'True' : 'False'}
                          {submitted && isThisCorrect && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Review: Show correct answer when wrong */}
                {submitted && isWrong && (
                  <div data-testid={`quiz-review-${q.id}`} className="ml-10 mt-3 flex items-start gap-2 p-3 bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800 rounded-md">
                    <Eye className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                    <p className="text-sm text-emerald-700 dark:text-emerald-300">
                      <span className="font-semibold">Correct answer:</span> {getCorrectLabel(q)}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {!submitted && (
          <div className="mt-8 flex justify-end">
            <Button
              data-testid="quiz-submit-btn"
              onClick={handleSubmit}
              disabled={!allAnswered}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8"
            >
              Submit Answers
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
